import React, { useState, useEffect } from "react";
import {
  getDictationDetails,
  getDictationListBySearch,
} from "apiHandler/actions";
import { useUserData } from "components/customHooks/userDataContext";
import AdvanceSearchDictation from "components/modals/AdvanceSearchDictation";
import ListTable from "components/scribe/listTable";
import DictationSummary from "components/UI/DictationSummary";
import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";

const formControlStyles = {
  marginLeft: 1,
  marginRight: 1,
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#d91a5f", // Default border color
    },
    "&:hover fieldset": {
      borderColor: "#d91a5f", // Border color on hover
    },
    "&.Mui-focused fieldset": {
      borderColor: "#d91a5f", // Border color when focused
    },
  },
};

const inputLabelStyles = {
  color: "#d91a5f", // Default label color
  "&.Mui-focused": {
    color: "#d91a5f", // Label color when focused
  },
};

const selectStyles = {
  padding: "2px 8px", // Adjust padding to reduce height
  "& .MuiSelect-select": {
    padding: "6px 10px", // Adjust padding for the select area
    lineHeight: "1.5", // Adjust line height
  },
};

const menuItemStyles = {
  padding: "8px 10px", // Adjust padding for MenuItems
};

const Dictations = () => {
  const [dictationList, setDictationList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPlaying, setCurrentPlaying] = useState(null);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const [printPage, setPrintPage] = useState(null);
  const [loadingPrint, setLoadingPrint] = useState(false);
  const [dosToAndFrom, setDosToAndFrom] = useState([]);
  const [selectedDictationIds, setSelectedDictationIds] = useState([]);

  const [status, setStatus] = useState("All");

  const userData = useUserData();
  const token = userData?.access_token;

  const fetchDictations = async (extraPayload = {}, search = false) => {
    try {
      setLoading(true);

      // Clear old UI state immediately
      setDictationList([]);
      setSelectedDictationIds([]);
      setCurrentPlaying(null);

      const payload = {
        type: "Scribe",
        search_criteria: {
          practice_id: userData?.selected_practice,
          ...extraPayload,
        },
      };

      setIsSearched(search);

      if ("dos_from" in extraPayload && "dos_to" in extraPayload) {
        setDosToAndFrom([extraPayload.dos_from, extraPayload.dos_to]);
      } else {
        setDosToAndFrom([]);
      }

      const response = await getDictationListBySearch(payload, token);

      setDictationList(response || []);
    } catch (error) {
      console.log("error:", error);
      setDictationList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDictations();
  }, []);

  const handlePrintSummary = async () => {
    setLoadingPrint(true);
    let searchCriteria = {};
    if (selectedDictationIds.length) {
      searchCriteria = {
        practice_id: userData?.selected_practice,
        dictation_id_list: selectedDictationIds,
      };
    } else if (dosToAndFrom.length) {
      searchCriteria = {
        practice_id: userData?.selected_practice,
        dos_from: dosToAndFrom[0],
        dos_to: dosToAndFrom[1],
      };
    } else {
      searchCriteria = {
        practice_id: userData?.selected_practice,
        dictation_id: dictationList.length && dictationList[0].id.toString(),
      };
    }
    const payload = {
      type: "Dictation",
      search_criteria: searchCriteria,
    };
    const dictationDetailResponse = await getDictationDetails(payload, token);
    if (dictationDetailResponse) {
      setPrintPage(
        dictationDetailResponse.map((detail) => ({
          practice_details: detail.practice_details,
          patient_details: detail.patient_details,
          notes: detail.notes,
          dos: detail.dos,
        })),
      );
      window.print();
      setLoadingPrint(false);
      setPrintPage(null);
    } else {
      // setSnackbarMessage("Failed to get Dictation Details");
    }
    setLoadingPrint(false);
    setPrintPage(null);
  };

  const handleSelectedDictationIds = (ids) => {
    setSelectedDictationIds(ids);
  };

  const handleChange = async (event) => {
    const newStatus = event.target.value;
    console.log("event.target.value: ", newStatus);

    // Update the status state
    setStatus(newStatus);
    const payload = newStatus === "All" ? {} : { status: newStatus };
    try {
      await fetchDictations(payload);
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const onSearch = async (extraPayload) => {
    const payload = { ...extraPayload };

    if (status !== "All") {
      payload.status = status;
    }
    try {
      await fetchDictations(payload, true);
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const onClear = () => {
    setIsSearched(false);
    const payload = status === "All" ? {} : { status };
    setDosToAndFrom([]);
    fetchDictations(payload);
  };

  return (
    <>
      <div className="dictations-list-page">
        <main>
          <div
            style={{ padding: "16px", borderRadius: "8px" }}
            className="header"
          >
            {/* <div className="calendar-header-count"> */}
            {/* <div className="cal-diff-search">
              <span>DOS</span>
              <KeyboardDatePicker
                format="MM/dd/yyyy"
                placeholder="MM/DD/YYYY"
                value={start_dos}
                onChange={(e) => setStart_dos(e)}
                autoOk
              />
              <button onClick={onSearch} className="blank-hover-btn">
                Search
              </button>
            </div> */}
            {/* </div> */}
            <FormControl sx={formControlStyles}>
              <InputLabel sx={inputLabelStyles}>Status</InputLabel>
              <Select
                value={status}
                label="Status"
                onChange={handleChange}
                sx={selectStyles}
              >
                <MenuItem value="All" sx={menuItemStyles}>
                  All
                </MenuItem>
                <MenuItem value="Completed" sx={menuItemStyles}>
                  Completed
                </MenuItem>
                <MenuItem value="In-Progress" sx={menuItemStyles}>
                  In-Progress
                </MenuItem>
                <MenuItem value="In-Coding" sx={menuItemStyles}>
                  In-Coding
                </MenuItem>
              </Select>
            </FormControl>
            <button
              onClick={() => handlePrintSummary()}
              className="blank-hover-btn"
              style={{ marginRight: 10 }}
            >
              {loadingPrint ? (
                <CircularProgress color="secondary" size={15} />
              ) : (
                "Print Summary Form"
              )}
            </button>
            <button
              onClick={() => setShowSearchModal(true)}
              className="blank-hover-btn"
            >
              Advance Search
            </button>
            {isSearched ? (
              <button
                onClick={onClear}
                className="blank-hover-btn"
                style={{ marginLeft: 10 }}
              >
                Clear
              </button>
            ) : null}
            {showSearchModal ? (
              <AdvanceSearchDictation
                onClose={() => setShowSearchModal(false)}
                onSearch={onSearch}
              />
            ) : null}
          </div>
        </main>
      </div>
      <main>
        <ListTable
          records={dictationList}
          currentPlaying={currentPlaying}
          setCurrentPlaying={setCurrentPlaying}
          status="Test"
          loading={loading}
          checkboxSelection={true}
          handleSelectedDictationIds={handleSelectedDictationIds}
        />
      </main>
      {/* </main> */}
      {/* <DataGrid
        rows={loading ? [] : dictationList}
        columns={columns}
        autoHeight="true"
        disableSelectionOnClick
        pagination
        pageSize={50}
        density="compact"
        components={{
          Pagination: DataGridCustomPagination,
        }}
        loading={loading}
      /> */}
      {/* </div> */}
      {printPage === null ? null : (
        <>
          {printPage.map((params, i) => {
            return <DictationSummary key={i} params={params} />;
          })}
        </>
      )}
    </>
  );
};

export default Dictations;
